import React, { useMemo } from 'react';
import WarningIcon from '@atlaskit/icon/utility/warning';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { useGetLinkProps } from '@atlassian/jira-navigation-apps-sidebar-nav4-common/src/common/utils/get-link-props/index.tsx';
import type { ProjectContext } from '@atlassian/jira-router-resources-project-context/src/services/project-context/types.tsx';
import { routeGroupsProjectSettingsServicedeskLanguageKeyRoute } from '@atlassian/jira-router-routes-project-settings-product-routes-service-desk-routes';
import {
	MenuList,
	MenuLinkItem,
	ExpandableMenuItem,
	ExpandableMenuItemTrigger,
	ExpandableMenuItemContent,
} from '@atlassian/navigation-system';
import { useTranslationGroups } from '../../controllers/manage-translations/index.tsx';
import { messages } from './messages';

type SidebarV4TranslationsPanelProps = Pick<ProjectContext, 'projectKey'>;

export const SidebarV4TranslationsPanel = ({ projectKey }: SidebarV4TranslationsPanelProps) => {
	const { formatMessage } = useIntl();

	const { translationGroups, currentLanguage } = useTranslationGroups();
	const { getLinkPropsFromRoute } = useGetLinkProps();

	const translationGroupsWithChildHrefs = useMemo(
		() =>
			translationGroups.map((group) => ({
				...group,
				panels: group.panels.map((panel) => ({
					...panel,
					...getLinkPropsFromRoute(routeGroupsProjectSettingsServicedeskLanguageKeyRoute, {
						projectKey,
						languageKey: currentLanguage,
						sidebarItemKey: panel.id,
					}),
				})),
			})),
		[translationGroups, getLinkPropsFromRoute, projectKey, currentLanguage],
	);

	if (translationGroupsWithChildHrefs.length === 0) {
		return null;
	}

	return (
		<MenuList>
			{translationGroupsWithChildHrefs.map((group) => (
				<ExpandableMenuItem
					key={group.name}
					isDefaultExpanded={group.panels.some((panel) => panel.isSelected)}
				>
					<ExpandableMenuItemTrigger
						elemAfter={
							group.panels.some((panel) => panel.isFlagged) && (
								<WarningIcon
									color={token('color.icon.warning')}
									label={formatMessage(messages.warningIconAccessibilityLabel)}
								/>
							)
						}
					>
						{group.name}
					</ExpandableMenuItemTrigger>
					<ExpandableMenuItemContent>
						{group.panels.map(({ id, isFlagged, displayName, isSelected, href }) => (
							<MenuLinkItem
								key={id}
								analytics={{ itemId: id }}
								elemAfter={
									isFlagged && (
										<WarningIcon
											color={token('color.icon.warning')}
											label={formatMessage(messages.warningIconAccessibilityLabel)}
										/>
									)
								}
								href={href}
								isSelected={isSelected}
							>
								{displayName}
							</MenuLinkItem>
						))}
					</ExpandableMenuItemContent>
				</ExpandableMenuItem>
			))}
		</MenuList>
	);
};
